import {debounceTime} from 'rxjs/operators';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {BaseMapComponent} from '../../common/base-map/base-map.component';
import {CITY, Colors, Mode, Source} from '../../common/const';
import {ComponentLoaderService} from 'app/common/component-loader.service';
import {MapContextService} from 'app/common/map-context.service';
import {RealTimeService} from 'app/real-time/real-time/real-time.service';
import {RealTimeContextService} from 'app/real-time/real-time-context.service';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {environment} from '../../../environments/environment';
import {Subscription} from 'rxjs/Subscription';
import {LAYERS_ID} from './layers/constants';
import {WazeLayer} from './layers/wazeLayer';
import {LayerService} from '../../common/services/layer.service';
import {BaseLayerDeck} from '../../common/layers/baseLayerDeck';
import {MvtService} from '../../common/services/mvt.service';

export enum VIEW_MODES {
    LIVE = 'live',
    UPCOMING = 'upcoming',
    PLATES = 'plates',
    ROUTES = 'route',
}

@Component({
    selector: 'app-real-time',
    templateUrl: './real-time.component.html',
    styleUrls: ['./real-time.component.scss'],
})
export class RealTimeComponent extends BaseMapComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('mapContainer') mapContainer;
    @ViewChild('zoomIn') zoomIn: ElementRef;
    @ViewChild('zoomOut') zoomOut: ElementRef;

    colorPrimaryLight = Colors.colorPrimaryLight;
    stats: any;

    loading = true;
    selectedMode;

    protected source = Source;
    protected mode = Mode;
    protected categories: any;
    protected layers: { [layer_id: string]: { layer: BaseLayerDeck, modes: string[] } } = {};
    protected subscription: Subscription;
    protected layersLoaded;
    protected interval;

    constructor(
        private viewContainer: ViewContainerRef,
        private realTimeService: RealTimeService,
        private realTimeContextService: RealTimeContextService,
        private componentLoaderService: ComponentLoaderService,
        private layerService: LayerService,
        protected mapContext: MapContextService,
        private mvtService: MvtService,
        @Inject(CITY) private city,
        private route: ActivatedRoute,
        private zone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        super(mapContext, zone);
    }

    ngOnInit() {
        super.ngOnInit();
        this.layersLoaded = false;
        // Filter by options defined in environment
        this.mapContext.listenerLayerLoading.subscribe((e: Array<any>) => {
            if (e.length) {
                this.loading = false;
            }
        });
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = new Subscription();
        this.subscription.add(
            this.mapContext.elementsForPopup$.pipe(debounceTime(200)).subscribe((popups) => this.openPopup(popups))
        );
        this.subscription.add(
            this.mapContext.onViewRender.pipe(debounceTime(200)).subscribe(() => this.updateCounters())
        );

        this.map.on('load', () => {
            this.componentLoaderService.setRootViewContainerRef(this.viewContainer);
            this.componentLoaderService.loadComponentDynamically();
            this.subscription.add(
                this.realTimeContextService.realTimeLayersContext$
                    .subscribe(([mode, source, categories]) => this.updateComponentView(mode, source, categories))
            );

        });

        this.route.queryParamMap.subscribe((paramMap: ParamMap) => {
            this.refreshMode(paramMap.get('panel') || 'live');
            this.mapContext.closePopup();
            this.realTimeContextService.setMode(this.selectedMode);

        });

        this.subscription.add(
            this.realTimeContextService.cityVariables.subscribe(city => {
                this.selectedMode = {...city};
                this.cdr.detectChanges();
            })
        );
    }

    updateComponentView(mode, source, categories) {
        if (this.layersLoaded) {
            this.updateLayerView(mode);
        }

        if (!this.layersLoaded) {
            this.initLayers(this.selectedMode);
            this.layersLoaded = true;
        }


        if (this.categories) {
            this.updateLayerFilters(mode);
        }
        this.categories = categories;
        this.source = source;

        clearInterval(this.interval);
        if (this.selectedMode.mode === VIEW_MODES.LIVE) {
            this.interval = setInterval(() => {
                if (this.layerService.hasLayer(LAYERS_ID.WAZE_LAYER)) {
                    this.layerService.getLayer(LAYERS_ID.WAZE_LAYER).updateSource();
                }
            }, 120000);
        }
    }

    initLayers(mode) {
        const modes = this.city.map(c => c.mode);
        const layers: any[] = this.getLayers()
            .filter(l => l.modes.some(m => modes.includes(m)));

        if (!environment.overrideWazeAlerts) {
            layers.push({
                id: LAYERS_ID.WAZE_LAYER,
                layer: new WazeLayer(this.city, this.mvtService),
                modes: [VIEW_MODES.LIVE],
            });


        }
        this.layers = {};
        layers.forEach(l => {
            this.layers[l.id] = {
                layer: this.layerService.addLayer(
                    l.layer,
                    l.modes.includes(mode.mode),
                    l.underLayerId,
                ),
                modes: l.modes
            };
            this.subscription.add(
                this.layers[l.id].layer.onHover.pipe(debounceTime(10)).subscribe((hover) => {
                    this.mapContext.deckInstance.setProps({getCursor: () => hover ? 'pointer' : ''});
                })
            );
        });
    }

    updateLayerView(mode) {
        Object.keys(this.layers).forEach(id => {
            const l = this.layers[id];
            if (l.modes.includes(mode.mode) && !l.layer.visible) {
                this.layerService.showLayer(id);
            }
            if (!l.modes.includes(mode.mode) && l.layer.visible) {
                this.layerService.hideLayer(id);
            }
        });


        clearInterval(this.interval);
        if (!environment.overrideWazeAlerts && this.selectedMode.mode === VIEW_MODES.LIVE) {
            this.interval = setInterval(() => {
                if (this.layerService.hasLayer(LAYERS_ID.WAZE_LAYER)) {
                    this.layerService.getLayer(LAYERS_ID.WAZE_LAYER).updateSource();
                }
            }, 120000);
        }
    }

    updateLayerFilters(mode) {
        Object.keys(this.layers).forEach(id => {
            const l = this.layers[id];
            if (l.modes.includes(mode.mode) && l.layer.visible) {
                this.layerService.getLayer(id).updateFilters(this.city);
            }
        });
    }

    updateCounters() {
        Object.keys(this.layers).forEach(id => {
            const l = this.layers[id];
            if (l.modes.includes(this.selectedMode.mode) && l.layer.visible) {
                l.layer.updateCounters();
                const city = this.city.find(c => c.mode === this.selectedMode.mode);
                this.realTimeContextService.cityVariables.next(city);
            }
        });
    }

    getLayers() {
        return [];
    }

    refreshMode(mode) {
        const metadata = this.city.find(_meta => {
            return _meta.mode === mode;
        });
        if (metadata) {
            this.changeMode(metadata);
        }
    }

    changeMode(metadata) {
        this.city.forEach(_metadata => {
            _metadata.active = metadata.mode === _metadata.mode;
        });
        this.selectedMode = null;
        this.selectedMode = {...metadata};
        this.selectedMode.sources = this.selectedMode.sources.filter(src => environment.options.sources.includes(src.id));
    }

    ngAfterViewInit() {
        this.zoomIn.nativeElement.onclick = this.zoomInFn.bind(this);
        this.zoomOut.nativeElement.onclick = this.zoomOutFn.bind(this);
    }

    ngOnDestroy() {

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        clearInterval(this.interval);
    }

    zoomInFn() {
        this.mapContext.zoomIn();
    }

    zoomOutFn() {
        this.mapContext.zoomOut();
    }

}



// WEBPACK FOOTER //
// ./src/app/real-time/real-time/real-time.component.ts